import React, { useMemo, useState } from 'react';
import { useHits, useSearchBox } from 'react-instantsearch-core';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { styled } from 'styled-components';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import {
  IGNORED_ALGOLIA_SEARCH_URLS,
  NAV_MENU_BREAKPOINT,
  NAV_SEARCH_LANDINGS_INITIAL_COUNT,
  NAV_SEARCH_LANDINGS_SHOW_MORE_COUNT,
  NAV_SEARCH_PRODUCTS_INITIAL_COUNT,
  NAV_SEARCH_VITALS_INITIAL_COUNT
} from '@constants';
import { AlgoliaHit } from '@contracts';
import NavSearchArticleHits from './NavSearchArticleHits';
import NavSearchLandingHits from './NavSearchLandingHits';
import NavSearchProductHits from './NavSearchProductHits';

const NavCustomSearchResults = () => {
  const { query } = useSearchBox();
  const { hits } = useHits<AlgoliaHit>();
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [productResultsCount, setProductResultsCount] = useState(0);
  const [vitalsResultsCount, setVitalsResultsCount] = useState(0);
  const uniqueTitlesSet = new Set();

  const { t } = useTranslation();

  const isIgnoredByUrl = (hit: AlgoliaHit) => {
    return !hit?.path || IGNORED_ALGOLIA_SEARCH_URLS.includes(hit.path);
  };

  const isIgnoredByTitle = (hit: AlgoliaHit) => {
    if (!hit?.title) return true;

    if (uniqueTitlesSet.has(hit.title)) {
      return true;
    }

    uniqueTitlesSet.add(hit.title);

    return false;
  };

  const isProductHit = (hit: AlgoliaHit) => {
    return hit.category === 'product';
  };

  const isArticleHit = (hit: AlgoliaHit) => {
    return hit.category === 'article';
  };

  const productHits = useMemo(
    () =>
      hits.filter((hit) => {
        return isProductHit(hit);
      }),
    [hits]
  );

  const vitalHits = useMemo(
    () =>
      hits.filter((hit) => {
        return isArticleHit(hit);
      }),
    [hits]
  );

  const landingHits = useMemo(
    () =>
      hits
        .filter((hit) => {
          const isCategoryMatch = ['landing', 'press', 'product_category', 'article_category'].includes(hit.category);
          const image = hit.image || hit.meta_image;

          return image && isCategoryMatch && !isIgnoredByTitle(hit) && !isIgnoredByUrl(hit);
        })
        .map((hit) => ({ ...hit, title: hit?.title, image: hit.image || hit.meta_image })),
    [hits]
  );

  const searchResultsCount = useMemo(
    () => (landingHits?.length || 0) + productResultsCount + vitalsResultsCount,
    [landingHits, productResultsCount, vitalsResultsCount]
  );

  const additionalResults = useMemo(() => {
    const allAdditionalHits = [...landingHits];

    return allAdditionalHits.slice(0, shouldLoadMore ? NAV_SEARCH_LANDINGS_SHOW_MORE_COUNT : NAV_SEARCH_LANDINGS_INITIAL_COUNT);
  }, [landingHits, shouldLoadMore]);

  return (
    <Wrapper>
      {query ? <Title>{`${t(searchResultsCount ? 'components.search-results-for' : 'components.no-results-for')} "${query}"`}</Title> : null}
      {productResultsCount ? <CategoryTitle>{t('components.products')}</CategoryTitle> : null}
      <NavSearchProductHits hits={productHits} query={query} shouldLoadMore={shouldLoadMore} setProductResultsCount={setProductResultsCount} />
      {vitalsResultsCount ? <CategoryTitle>{t('components.vitals-only')}</CategoryTitle> : null}
      <NavSearchArticleHits hits={vitalHits} query={query} shouldLoadMore={shouldLoadMore} setVitalsResultsCount={setVitalsResultsCount} />
      {additionalResults?.length ? <CategoryTitle>{t('components.additional-results')}</CategoryTitle> : null}
      <NavSearchLandingHits hits={additionalResults} />
      {productResultsCount > NAV_SEARCH_PRODUCTS_INITIAL_COUNT ||
      vitalsResultsCount > NAV_SEARCH_VITALS_INITIAL_COUNT ||
      additionalResults?.length > NAV_SEARCH_LANDINGS_INITIAL_COUNT ? (
        <ShowMoreButtonWrapper>
          {searchResultsCount ? (
            <Button onClick={() => setShouldLoadMore((state) => !state)}>
              {t(shouldLoadMore ? 'components.show-less' : 'components.show-more')}
            </Button>
          ) : null}
        </ShowMoreButtonWrapper>
      ) : null}
    </Wrapper>
  );
};

export default NavCustomSearchResults;

const Wrapper = styled.div`
  padding-bottom: 300px;
`;

const ShowMoreButtonWrapper = styled.div`
  display: flex;
  margin: 30px 0;
  justify-content: center;
`;

const Title = styled.div`
  margin-top: 32px;
  color: ${Colors.navyBlue};
  text-align: center;
  font-family: Domaine;
  font-size: 40px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.4px;

  @media (max-width: ${NAV_MENU_BREAKPOINT}px) {
    margin-top: 24px;
    font-size: 32px;
    text-align: left;
  }
`;

const CategoryTitle = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  color: ${Colors.navyBlue};
  font-family: Domaine;
  font-size: 32px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.32px;

  @media (max-width: ${NAV_MENU_BREAKPOINT}px) {
    font-size: 24px;
    margin-bottom: 16px;
  }
`;
